import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Atlas from '../components/Globe3D';
import VisitorCounter from '../components/VisitorCounter';
import { AGENCIES, VIDEOS, TIMELINE } from '../mock';
import { fetchDossiers, fetchStats, imageUrl } from '../api';
import {
  ArrowRight, FileText, Film, Clock, Mic2, Globe2, Sparkles, ChevronRight,
  Unlock, Loader2, ShieldAlert, ScrollText, Radar, Flame, Landmark, Syringe
} from 'lucide-react';
import { usePortalData } from '../hooks/usePortalData';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_dossiers-ovni/artifacts/v12o9i5g_7a5e02bb1078ef123e92123dcc0b21d5.jpg';

const ICON_MAP = {
  ufo: Radar,
  classified: ShieldAlert,
  executive: ScrollText,
  virus: Syringe,
};

function EntityCard({ section, index }) {
  const Icon = ICON_MAP[section.icon] || Landmark;
  const accent = section.accent_color || '#fbbf24';
  const accentSlug = section.id;

  return (
    <motion.article
      data-testid={`entity-card-${section.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="entity-card flex flex-col h-full"
      style={{
        borderColor: `${accent}44`,
      }}
    >
      <div
        className="entity-header-stripe"
        style={{ background: `linear-gradient(90deg, ${accent}, ${accent}80)` }}
      />

      <div className="flex items-center justify-between mb-3">
        <span className="mono text-[10px] tracking-[0.28em] uppercase" style={{ color: accent }}>
          {section.label}
        </span>
        <span
          className="serif text-3xl font-light leading-none opacity-40"
          style={{ color: accent }}
        >
          {section.number}
        </span>
      </div>

      <div className="flex items-start gap-3 mb-4">
        <div
          className="h-11 w-11 rounded border shrink-0 flex items-center justify-center"
          style={{ borderColor: `${accent}55`, background: `${accent}12` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <h3 className="serif text-2xl md:text-[1.65rem] text-1 leading-tight">
            {section.title}
          </h3>
          <div className="mono text-[10.5px] text-3 mt-1 tracking-wider uppercase">
            {section.subtitle}
          </div>
        </div>
      </div>

      <p className="text-2 text-[14.5px] leading-relaxed mb-5">
        {section.description}
      </p>

      {section.stats && section.stats.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-5">
          {section.stats.map((s, i) => (
            <div
              key={i}
              className="rounded-md p-2.5 border-l-2 bg-black/25"
              style={{ borderLeftColor: accent }}
            >
              <div className="mono text-[9px] tracking-widest text-3 uppercase truncate">{s.label}</div>
              <div className="serif text-lg text-1 leading-tight mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-2 pt-3 border-t border-white/5">
        {section.cta_primary && (
          <Link
            to={section.cta_primary.to}
            data-testid={`entity-cta-${accentSlug}-primary`}
            className="px-3.5 py-2 rounded-md text-[13px] inline-flex items-center gap-1.5 transition-all"
            style={{
              background: `linear-gradient(180deg, ${accent}22, ${accent}10)`,
              border: `1px solid ${accent}66`,
              color: accent,
            }}
          >
            {section.cta_primary.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
        {section.cta_secondary && (
          <Link
            to={section.cta_secondary.to}
            data-testid={`entity-cta-${accentSlug}-secondary`}
            className="btn-ghost px-3.5 py-2 rounded-md text-[13px] inline-flex items-center gap-1.5"
          >
            {section.cta_secondary.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </motion.article>
  );
}

export default function HomePage() {
  const { data: portalData, loading: portalLoading } = usePortalData();
  const sections = portalData?.sections || [];
  const [featured, setFeatured] = useState([]);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_agency: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [{ items }, st] = await Promise.all([
          fetchDossiers({ limit: 24 }),
          fetchStats(),
        ]);
        if (cancel) return;
        setFeatured(items.slice(0, 6));
        setFeed(items.slice(0, 8));
        setStats(st);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const highlights = TIMELINE.filter(t => t.highlight).slice(0, 5);

  return (
    <div>
      {/* HERO — ARCHIVES SOUVERAINES */}
      <section className="relative overflow-hidden border-b border-white/5" data-testid="hero-archives-souveraines">
        {/* Hero image backdrop */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 35%',
            filter: 'brightness(0.35) contrast(1.05) saturate(0.85)',
          }}
        />
        {/* Overlay gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(30,30,30,0.35) 0%, rgba(45,45,45,0.6) 45%, rgba(45,45,45,0.95) 100%)',
          }}
        />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />

        <div className="mx-auto max-w-[1400px] px-6 pt-14 pb-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.28em] text-amber-300/90 uppercase mb-6 px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 backdrop-blur">
              <Sparkles className="h-3 w-3" /> Portail Officieux · Accès Public · Sans Mot de Passe
            </div>

            <h1 className="serif font-medium leading-[0.95] tracking-tight mb-4 drop-shadow-2xl">
              <span className="block text-4xl md:text-6xl lg:text-7xl">
                <span className="sovereign-title">ARCHIVES</span>
              </span>
              <span className="block text-4xl md:text-6xl lg:text-7xl">
                <span className="sovereign-title">SOUVERAINES</span>
              </span>
              <span className="block mt-4 text-xl md:text-2xl lg:text-3xl text-1 font-light italic">
                La Divulgation Totale
              </span>
            </h1>

            <p className="text-2 mt-5 text-[15px] md:text-base max-w-3xl leading-relaxed">
              Portail français indépendant compilant les archives d'État déclassifiées.
              Trois entités, un même combat pour la transparence :
              <span className="text-violet-300"> OVNIs & Surnaturel</span>,
              <span className="text-red-300"> Déclassifications CIA / FBI</span>,
              <span className="text-amber-300"> Ordres Exécutifs</span>.
              Traductions IA, textes intégraux, aucune télémétrie.
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-6">
              <Link
                to="/ordres-executifs"
                data-testid="hero-cta-eo"
                className="btn-primary px-4 py-2.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5"
                style={{
                  background: 'linear-gradient(180deg, rgba(251,191,36,0.25), rgba(251,191,36,0.12))',
                  border: '1px solid rgba(251,191,36,0.5)',
                  color: '#fef3c7',
                }}
              >
                <ScrollText className="h-3.5 w-3.5" /> Ordres Exécutifs — Armes du Combat
              </Link>
              <Link
                to="/archives"
                className="btn-ghost px-4 py-2.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5"
              >
                <FileText className="h-3.5 w-3.5" /> Explorer les dossiers UAP
              </Link>
              <div className="hidden md:flex items-center gap-2 mono text-[10px] text-3 px-2 py-2">
                <Unlock className="h-3 w-3 text-emerald-400" /> Accès public · Sans télémétrie
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom classified strip */}
        <div className="eo-classified-strip" />
      </section>

      {/* 4 ENTITÉS — LE CŒUR DU PORTAIL (2×2 grid, dynamic from JSON) */}
      <section className="relative" data-testid="entities-section">
        <div className="mx-auto max-w-[1400px] px-6 pt-14 pb-16">
          <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="mono text-[10px] tracking-[0.28em] text-amber-300/90 uppercase mb-2 inline-flex items-center gap-2">
                <Landmark className="h-3 w-3" /> Quatre Entités · Une Même Souveraineté
              </div>
              <h2 className="serif text-3xl md:text-4xl text-1">
                Le Portail des <span className="text-amber-300">Archives d'État</span>
              </h2>
              <p className="text-2 text-sm mt-2 max-w-2xl">
                Chaque entité regroupe une catégorie distincte d'archives déclassifiées, avec ses propres
                dossiers, traductions et références officielles.
              </p>
            </div>
          </div>

          {portalLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="entities-loading">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="entity-card animate-pulse min-h-[280px] flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-300/60" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="entities-grid">
              {sections.map((s, i) => (
                <EntityCard key={s.id} section={s} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* LIVE FEED + STATS */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 pb-12">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 min-w-0">
              <div className="mb-3 flex items-end justify-between">
                <div>
                  <div className="mono text-[10px] tracking-[0.2em] text-amber-300/90 uppercase mb-1 inline-flex items-center gap-1.5">
                    <Flame className="h-3 w-3" /> Statistiques globales
                  </div>
                  <h2 className="serif text-2xl md:text-3xl">Volume archivé</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {AGENCIES.map(a => (
                  <div key={a.id} className="glass rounded-md p-2.5 border-l-2 min-w-0" style={{ borderLeftColor: a.color }}>
                    <div className="mono text-[9px] tracking-widest text-3 uppercase truncate">{a.label}</div>
                    <div className="flex items-baseline gap-1.5 mt-0.5">
                      <div className="serif text-2xl text-1 leading-none">{stats.by_agency?.[a.id] ?? a.count}</div>
                      <div className="text-3 text-[10px]">dossiers</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="glass rounded-lg p-3 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="mono text-[9px] tracking-widest text-amber-300 uppercase">· Live Feed</div>
                  <div className="flex items-center gap-1.5 mono text-[9px] text-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> SYNC {stats.total || '494'} DOCS
                  </div>
                </div>
                <div className="space-y-0.5 max-h-[280px] overflow-auto pr-1">
                  {loading ? (
                    <div className="text-3 text-xs inline-flex items-center gap-2 px-2 py-3">
                      <Loader2 className="h-3 w-3 animate-spin" /> Chargement…
                    </div>
                  ) : feed.map((d, i) => (
                    <Link
                      to={`/dossier/${d.id}`}
                      key={d.id}
                      className="flex items-start gap-2 p-1.5 rounded-md hover:bg-white/5 transition-colors group"
                    >
                      <div className="mono text-[9px] text-3 w-7 shrink-0 pt-0.5">{String(i + 1).padStart(3, '0')}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span
                            className="mono text-[9px] px-1 py-px rounded"
                            style={{
                              color: AGENCIES.find(a => a.id === d.agency)?.color,
                              border: `1px solid ${AGENCIES.find(a => a.id === d.agency)?.color}40`,
                            }}
                          >
                            {AGENCIES.find(a => a.id === d.agency)?.short}
                          </span>
                          <span className="mono text-[9px] text-3">{d.date || '—'}</span>
                        </div>
                        <div className="text-xs text-1 truncate group-hover:text-amber-200">{d.title}</div>
                      </div>
                      <ChevronRight className="h-3 w-3 text-3 group-hover:text-amber-300 mt-1" />
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ATLAS compact */}
      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-6 pb-10">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="mono text-[10px] tracking-[0.2em] text-violet-300/90 uppercase mb-1 inline-flex items-center gap-1.5">
                <Globe2 className="h-3 w-3" /> Atlas Global · 1947 — 2026
              </div>
              <h2 className="serif text-2xl md:text-3xl">Incidents géolocalisés</h2>
            </div>
            <Link to="/archives" className="text-amber-300 text-sm inline-flex items-center gap-1 hover:text-amber-200">
              Toutes les archives <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <Atlas />
        </div>
      </section>

      {/* FEATURED compact */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 pb-10">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="mono text-[10px] tracking-[0.2em] text-amber-300/90 uppercase mb-1 inline-flex items-center gap-1.5">
                <FileText className="h-3 w-3" /> Dossiers en vedette
              </div>
              <h2 className="serif text-2xl md:text-3xl">Sélection traduite</h2>
            </div>
            <Link to="/archives" className="text-amber-300 text-sm inline-flex items-center gap-1 hover:text-amber-200">
              Tout voir <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {featured.map(d => {
              const agency = AGENCIES.find(a => a.id === d.agency);
              return (
                <Link key={d.id} to={`/dossier/${d.id}`} className="doc-card glass rounded-lg overflow-hidden">
                  <div className="relative aspect-[16/9] overflow-hidden bg-black">
                    {d.image_url && <img src={imageUrl(d.image_url)} alt={d.title} className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-500 hover:scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                      <span className="badge-classif" style={{ color: agency?.color }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: agency?.color }} /> {agency?.short}
                      </span>
                      <span className="mono text-[9px] text-2 bg-black/50 px-1.5 py-0.5 rounded">{d.classification}</span>
                    </div>
                    <div className="absolute bottom-2 left-2 mono text-[9px] text-2 truncate max-w-[80%]">{d.id.split('-').slice(0,2).join('-')} · {d.date || '—'}</div>
                  </div>
                  <div className="p-3">
                    <div className="text-1 text-sm font-medium leading-snug mb-1.5 line-clamp-2">{d.title}</div>
                    <p className="text-3 text-xs line-clamp-2">{d.summary}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                      <span className="mono text-[9px] text-3 truncate">{d.location || 'N/A'}</span>
                      <span className="text-amber-300 text-[11px] inline-flex items-center gap-1 shrink-0">Ouvrir <ArrowRight className="h-3 w-3" /></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS compact */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 pb-14">
          <div className="glass rounded-xl p-5 md:p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="mono text-[10px] tracking-[0.2em] text-violet-300/90 uppercase mb-1 inline-flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> Moments clés
                  </div>
                  <h2 className="serif text-2xl">80 ans en 5 dates</h2>
                </div>
                <Link to="/chronologie" className="text-amber-300 text-sm inline-flex items-center gap-1 hover:text-amber-200">
                  Chronologie complète <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <ol className="relative border-l border-white/10 space-y-4 pl-5">
                {highlights.map((t, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full bg-amber-300 ring-4 ring-amber-300/20" />
                    <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
                      <span className="serif text-xl text-amber-300">{t.year}</span>
                      <span className="mono text-[9px] text-3">{t.date}</span>
                    </div>
                    <div className="text-1 text-sm font-medium">{t.title}</div>
                    <div className="text-2 text-xs mt-0.5 max-w-3xl">{t.desc}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 pb-14">
          <div className="relative overflow-hidden rounded-xl glass-strong p-6 md:p-8">
            <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
            <div className="relative grid md:grid-cols-3 gap-5 items-center">
              <div className="md:col-span-2">
                <div className="mono text-[10px] tracking-[0.28em] text-amber-300/90 uppercase mb-2">
                  Transparence · Accès Public · Sans Mot de Passe
                </div>
                <h3 className="serif text-xl md:text-2xl mb-1.5">
                  Tous les dossiers, en français. En libre accès total.
                </h3>
                <p className="text-2 text-sm max-w-2xl">
                  ARCHIVES SOUVERAINES compile, traduit et contextualise les documents officiels
                  déclassifiés. Sources publiques, recherche locale, aucune télémétrie, aucun mot de passe.
                </p>
              </div>
              <div className="flex md:justify-end gap-2 flex-wrap">
                <Link
                  to="/ordres-executifs"
                  className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
                  style={{
                    background: 'linear-gradient(180deg, rgba(251,191,36,0.22), rgba(251,191,36,0.10))',
                    border: '1px solid rgba(251,191,36,0.5)',
                    color: '#fef3c7',
                  }}
                >
                  <ScrollText className="h-3.5 w-3.5" /> Ordres Exécutifs
                </Link>
                <Link to="/temoignages" className="btn-ghost px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5">
                  <Mic2 className="h-3.5 w-3.5" /> Témoignages
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VISITOR COUNTER */}
      <VisitorCounter />
    </div>
  );
}
